import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaDownload,
  FaEdit,
  FaTrash,
  FaCodeBranch,
  FaArrowLeft,
  FaCloudUploadAlt,
  FaHistory,
  FaInfoCircle,
  FaLock,
  FaGlobe,
  FaCalendarAlt,
  FaEye,
  FaFileAlt,
  FaCheckCircle,
} from "react-icons/fa";
import api from "../api/axios";
import "./DocumentManagement.css";
import { formatFileSize, getFileIcon, getExpiryBadge } from "./docUtils";

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [relatedDocs, setRelatedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "versions" ? "versions" : "info");

  // New version upload
  const [showVersionUpload, setShowVersionUpload] = useState(false);
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [newVersionNotes, setNewVersionNotes] = useState("");
  const [newVersionExpiry, setNewVersionExpiry] = useState("");
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const fileRef = useRef(null);

  // Delete
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDoc = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/document-management/${id}`);
      setDoc(res.data);
      // Fetch versions
      const vRes = await api.get(`/document-management/${id}/versions`);
      setVersions(vRes.data || []);
      // Fetch related docs if entity is set
      if (res.data.entity_type && res.data.entity_id) {
        const rRes = await api.get(`/document-management/entity/${res.data.entity_type}/${res.data.entity_id}`);
        setRelatedDocs((rRes.data || []).filter(d => d.id !== res.data.id));
      }
    } catch (e) {
      setError("Failed to load document record.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDoc(); }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/document-management/${id}`);
      navigate("/dashboard/documents/list");
    } catch (e) {
      setError("Failed to delete document.");
      setDeleting(false);
    }
  };

  const handleVersionUpload = async (e) => {
    e.preventDefault();
    if (!newVersionFile) return;
    setUploadingVersion(true);
    try {
      const fd = new FormData();
      fd.append("file", newVersionFile);
      if (newVersionNotes) fd.append("notes", newVersionNotes);
      if (newVersionExpiry) fd.append("expiry_date", newVersionExpiry);
      const user = localStorage.getItem("username") || "User";
      fd.append("uploaded_by", user);

      await api.post(`/document-management/${id}/new-version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowVersionUpload(false);
      setNewVersionFile(null);
      setNewVersionNotes("");
      setNewVersionExpiry("");
      fetchDoc();
    } catch (e) {
      setError("Failed to upload new revision.");
    } finally {
      setUploadingVersion(false);
    }
  };

  const isImage = (mime) => mime && mime.startsWith("image/");
  const isPdf = (mime) => mime && mime.includes("pdf");

  if (loading) return (
    <div className="doc-mgmt-container" style={{ textAlign: "center", paddingTop: 80 }}>
      <div className="spinner-border text-primary" />
      <div style={{ marginTop: 14, color: "#64748b", fontSize: "13px" }}>Loading document record...</div>
    </div>
  );

  if (error && !doc) return (
    <div className="doc-mgmt-container">
      <div className="alert alert-danger">{error}</div>
      <button className="doc-btn-outline" onClick={() => navigate("/dashboard/documents/list")}>
        <FaArrowLeft /> Back to Repository
      </button>
    </div>
  );

  if (!doc) return null;

  return (
    <div className="doc-mgmt-container">
      {/* Top Bar Actions */}
      <div className="doc-mgmt-header" style={{ marginBottom: 18 }}>
        <button className="doc-btn-outline" onClick={() => navigate("/dashboard/documents/list")}>
          <FaArrowLeft />
          <span>Back to Repository</span>
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <a
            href={doc.file_url}
            target="_blank"
            rel="noreferrer"
            className="doc-btn-outline"
            style={{ color: "#0284c7" }}
          >
            <FaDownload />
            <span>Download</span>
          </a>
          <button
            className="doc-btn-outline"
            onClick={() => navigate(`/dashboard/documents/edit/${id}`)}
          >
            <FaEdit />
            <span>Edit</span>
          </button>
          <button
            className="doc-btn-primary"
            onClick={() => { setActiveTab("versions"); setShowVersionUpload(true); }}
          >
            <FaCodeBranch />
            <span>New Version</span>
          </button>
          <button
            className="doc-btn-outline"
            style={{ color: "#dc2626" }}
            onClick={() => setShowDelete(true)}
            title="Delete Document"
          >
            <FaTrash />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-3">{error}</div>}

      {/* Main Container */}
      <div className="doc-detail-card">
        {/* Formal Header */}
        <div className="doc-detail-header-formal">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ fontSize: "36px", color: "#93c5fd" }}>
              {getFileIcon(doc.mime_type, doc.file_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 className="doc-detail-title-formal">{doc.title}</h2>
              <div className="doc-detail-meta-formal">
                {doc.file_name} • {formatFileSize(doc.file_size)} • Uploaded {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"}
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span className="doc-detail-tag">
                  {doc.category}
                </span>
                <span className="doc-detail-tag" style={{ textTransform: "capitalize" }}>
                  {doc.document_type}
                </span>
                {doc.is_latest_version && (
                  <span className="doc-detail-tag" style={{ background: "#059669", color: "#ffffff" }}>
                    <FaCheckCircle style={{ fontSize: "10px" }} /> Version {doc.version} (Active)
                  </span>
                )}
                {doc.is_private ? (
                  <span className="doc-detail-tag" style={{ background: "#991b1b" }}>
                    <FaLock style={{ fontSize: "10px" }} /> Restricted
                  </span>
                ) : (
                  <span className="doc-detail-tag">
                    <FaGlobe style={{ fontSize: "10px" }} /> Organization
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Strip */}
        <div style={{ borderBottom: "1px solid #e2e8f0", display: "flex", background: "#f8fafc" }}>
          {[
            { key: "info", label: "Overview", icon: <FaInfoCircle /> },
            { key: "preview", label: "File Preview", icon: <FaEye /> },
            { key: "versions", label: `Revisions (${versions.length})`, icon: <FaHistory /> },
            { key: "related", label: `Associated Files (${relatedDocs.length})`, icon: <FaFileAlt /> },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                border: "none",
                borderBottom: activeTab === tab.key ? "2px solid #0284c7" : "2px solid transparent",
                background: activeTab === tab.key ? "#ffffff" : "transparent",
                padding: "12px 20px",
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? "#0284c7" : "#64748b",
                cursor: "pointer",
                fontSize: "13px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="doc-detail-body">
          {/* OVERVIEW TAB */}
          {activeTab === "info" && (
            <div>
              <div className="doc-meta-grid">
                <div className="doc-meta-item">
                  <label>Uploaded By</label>
                  <span>{doc.uploaded_by || "System"}</span>
                </div>
                <div className="doc-meta-item">
                  <label>Registration Date</label>
                  <span>{doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"}</span>
                </div>
                <div className="doc-meta-item">
                  <label>MIME Content Type</label>
                  <span>{doc.mime_type || "Standard Binary"}</span>
                </div>
                <div className="doc-meta-item">
                  <label>Exact File Size</label>
                  <span>{formatFileSize(doc.file_size)}</span>
                </div>
                <div className="doc-meta-item">
                  <label>Revision Number</label>
                  <span>Revision {doc.version}</span>
                </div>
                <div className="doc-meta-item">
                  <label>Security Clearance</label>
                  <span>{doc.is_private ? "Restricted Access" : "Organization-Wide"}</span>
                </div>
              </div>

              {doc.entity_name && (
                <div style={{ marginBottom: 20, padding: "14px 18px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Associated CRM Entity
                  </div>
                  <div style={{ marginTop: 4, fontSize: "14px", color: "#0f172a" }}>
                    <span style={{ textTransform: "capitalize", color: "#475569" }}>{doc.entity_type}: </span>
                    <strong>{doc.entity_name}</strong>
                    {doc.entity_id && <span style={{ color: "#94a3b8", fontSize: "12px" }}> (Reference #{doc.entity_id})</span>}
                  </div>
                </div>
              )}

              {doc.has_expiry && (
                <div style={{ marginBottom: 20, padding: "16px 20px", background: doc.expiry_status === "expired" ? "#fff1f2" : doc.expiry_status === "expiring_soon" ? "#fffbeb" : "#f0fdf4", borderRadius: "8px", border: `1px solid ${doc.expiry_status === "expired" ? "#fecdd3" : doc.expiry_status === "expiring_soon" ? "#fde68a" : "#bbf7d0"}` }}>
                  <div style={{ marginBottom: 8, fontSize: "13px", fontWeight: 700, display: "flex", alignItems: "center", gap: 6, color: "#0f172a" }}>
                    <FaCalendarAlt style={{ color: "#0284c7" }} />
                    <span>Compliance & Expiry Information</span>
                  </div>
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: "13px" }}>
                    <span><strong>Scheduled Expiry:</strong> {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString() : "—"}</span>
                    <span><strong>Status:</strong> {getExpiryBadge(doc.expiry_status)}</span>
                    <span><strong>Notification Window:</strong> {doc.expiry_alert_days} days in advance</span>
                  </div>
                </div>
              )}

              {doc.description && (
                <div style={{ marginBottom: 20 }}>
                  <div className="doc-section-title">
                    <FaFileAlt /> Description
                  </div>
                  <p style={{ margin: 0, color: "#334155", fontSize: "13px", lineHeight: 1.6 }}>{doc.description}</p>
                </div>
              )}

              {doc.tags && (
                <div style={{ marginBottom: 20 }}>
                  <div className="doc-section-title">
                    <FaInfoCircle /> Indexing Tags
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {doc.tags.split(",").map(t => t.trim()).filter(Boolean).map(tag => (
                      <span key={tag} style={{ background: "#f1f5f9", color: "#475569", padding: "4px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, border: "1px solid #e2e8f0" }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {doc.notes && (
                <div>
                  <div className="doc-section-title">
                    <FaInfoCircle /> Administrative Notes
                  </div>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "12px 16px", borderRadius: "8px", fontSize: "13px", color: "#475569" }}>
                    {doc.notes}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === "preview" && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              {isImage(doc.mime_type) ? (
                <img
                  src={doc.file_url}
                  alt={doc.title}
                  style={{ maxWidth: "100%", maxHeight: 600, borderRadius: "8px", border: "1px solid #e2e8f0" }}
                />
              ) : isPdf(doc.mime_type) ? (
                <iframe
                  src={doc.file_url}
                  title={doc.title}
                  width="100%"
                  height="650px"
                  style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                />
              ) : (
                <div style={{ padding: 60, color: "#64748b" }}>
                  <div className="doc-empty-icon" style={{ width: 64, height: 64, fontSize: 32 }}>
                    {getFileIcon(doc.mime_type, doc.file_name)}
                  </div>
                  <div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a", marginTop: 8 }}>
                    Inline preview not supported for this file type
                  </div>
                  <p style={{ fontSize: "13px", color: "#64748b", margin: "6px 0 16px" }}>
                    Download the file to inspect its full contents locally.
                  </p>
                  <a href={doc.file_url} target="_blank" rel="noreferrer" className="doc-btn-primary">
                    <FaDownload /> Download File
                  </a>
                </div>
              )}
            </div>
          )}

          {/* REVISIONS TAB */}
          {activeTab === "versions" && (
            <div>
              {showVersionUpload ? (
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: 24, marginBottom: 24 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <span className="doc-section-title" style={{ margin: 0 }}>
                      <FaCodeBranch /> Upload New Document Revision
                    </span>
                    <button className="btn-close" onClick={() => setShowVersionUpload(false)} />
                  </div>
                  <form onSubmit={handleVersionUpload}>
                    <div className="mb-3">
                      <input
                        type="file"
                        ref={fileRef}
                        style={{ display: "none" }}
                        onChange={e => setNewVersionFile(e.target.files[0])}
                      />
                      <div
                        className="doc-upload-zone"
                        style={{ padding: 24 }}
                        onClick={() => fileRef.current?.click()}
                      >
                        <FaCloudUploadAlt style={{ fontSize: 32, color: "#0284c7", marginBottom: 6 }} />
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>
                          {newVersionFile ? newVersionFile.name : "Select new replacement file"}
                        </div>
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "12px", fontWeight: 600 }}>
                          Updated Expiry Date (Optional)
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={newVersionExpiry}
                          onChange={e => setNewVersionExpiry(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "12px", fontWeight: 600 }}>
                          Change Description / Revision Notes
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={newVersionNotes}
                          onChange={e => setNewVersionNotes(e.target.value)}
                          placeholder="e.g., Annual policy renewal, signed version"
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
                      <button type="button" className="doc-btn-outline" onClick={() => setShowVersionUpload(false)}>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="doc-btn-primary"
                        disabled={!newVersionFile || uploadingVersion}
                      >
                        {uploadingVersion ? "Uploading Revision..." : "Publish New Revision"}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    Complete revision history for compliance and auditing
                  </span>
                  <button className="doc-btn-primary" onClick={() => setShowVersionUpload(true)}>
                    <FaCodeBranch />
                    <span>Upload New Revision</span>
                  </button>
                </div>
              )}

              <div className="doc-version-history">
                {versions.length === 0 ? (
                  <div style={{ padding: 32, textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                    No prior revisions on record.
                  </div>
                ) : (
                  versions.map(v => (
                    <div key={v.id} className="doc-version-item">
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <span className={`doc-version-pill ${v.is_latest_version ? "latest" : ""}`}>
                          Rev {v.version}
                        </span>
                        <div>
                          <div style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>
                            {v.file_name}
                          </div>
                          <div style={{ fontSize: "11px", color: "#64748b", marginTop: 2 }}>
                            {formatFileSize(v.file_size)} • {v.created_at ? new Date(v.created_at).toLocaleString() : ""}
                            {v.uploaded_by && ` • by ${v.uploaded_by}`}
                          </div>
                          {v.notes && (
                            <div style={{ fontSize: "12px", color: "#475569", marginTop: 3 }}>
                              Note: {v.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {v.is_latest_version && (
                          <span style={{ fontSize: "11px", color: "#059669", fontWeight: 700 }}>
                            Active
                          </span>
                        )}
                        <a
                          href={v.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="doc-action-btn success"
                          title="Download this revision"
                        >
                          <FaDownload />
                        </a>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ASSOCIATED FILES TAB */}
          {activeTab === "related" && (
            <div>
              {relatedDocs.length === 0 ? (
                <div style={{ textAlign: "center", padding: 48, color: "#64748b" }}>
                  <FaFileAlt style={{ fontSize: 32, opacity: 0.3, marginBottom: 8 }} />
                  <p style={{ margin: 0, fontSize: "13px" }}>
                    No other documents linked to this {doc.entity_type || "entity"}.
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ color: "#475569", fontSize: "13px", marginBottom: 16 }}>
                    Other repository records associated with <strong>{doc.entity_name}</strong>:
                  </p>
                  {relatedDocs.map(rd => (
                    <div
                      key={rd.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        marginBottom: 10,
                        cursor: "pointer",
                        background: "#ffffff",
                      }}
                      onClick={() => navigate(`/dashboard/documents/${rd.id}`)}
                    >
                      {getFileIcon(rd.mime_type, rd.file_name)}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: "13px", color: "#0f172a" }}>{rd.title}</div>
                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: 2 }}>
                          {rd.category} • {formatFileSize(rd.file_size)}
                        </div>
                      </div>
                      {getExpiryBadge(rd.expiry_status)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <div className="modal show d-block" style={{ background: "rgba(15, 23, 42, 0.6)" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title" style={{ fontSize: "16px", fontWeight: 700, color: "#dc2626" }}>
                  Delete Document
                </h5>
                <button className="btn-close" onClick={() => setShowDelete(false)} />
              </div>
              <div className="modal-body pt-2">
                <p style={{ fontSize: "13px", color: "#334155" }}>
                  Delete <strong>{doc.title}</strong>? This action cannot be reversed.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="doc-btn-outline" onClick={() => setShowDelete(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  style={{ borderRadius: "8px", fontWeight: 600, padding: "8px 14px" }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
